import {
  EIBType,
  RequestCompensationType
} from '../NewRequest/ui/components/NewRequestForm/types';

export type TabDetails = 'correct' | 'errors' | 'logs';

export enum LogsActionEnum {
  ITEM_DELETED = 'ITEM_DELETED',
  REQUEST_ITEM_UPDATED = 'REQUEST_ITEM_UPDATED'
}

export type LogDetail = {
  id: string;
  message: string;
};

export type RequestCompensationDetail = {
  id?: string;
  lineNumber?: number;
  employeeId: string;
  action?: ActionsEnum;
  position: string;
  planCode: string;
  target: string;
  targetType: TargetTypeEnum;
  package?: string;
  grade?: string;
  gradeProfile?: string;
  step?: string;
  errorDetails?: string;
  logs?: LogDetail[];
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
};

export type RequestStatusPositionDetail = {
  id?: string;
  lineNumber?: number;
  action?: ActionsEnum;
  position: string;
  reason: string;
  errorDetails?: string;
  isDeleted?: boolean;
  deletedAt?: string;
  deletedBy?: string;
};

export type Reason = {
  name: string;
  code: string;
  workdayReasonId: string;
};

export enum ActionsEnum {
  FREEZE = 'FREEZE_POSITION',
  UNFREEZE = 'UNFREEZE_POSITION',
  CLOSE = 'CLOSE_POSITION'
}

export enum StatusEnum {
  OPEN = 'OPEN',
  REOPENED = 'REOPENED',
  REJECTED = 'REJECTED',
  COMPLETED = 'COMPLETED',
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  PARTIAL = 'PARTIAL',
  PROCESSING = 'PROCESSING'
}

export enum CategoryTabEnum {
  ALL = 'all',
  PENDING_APPROVAL = 'pendingApproval',
  AWAITING_ACTION = 'awaitingAction',
  COMPLETED = 'completed'
}

export enum StepEnum {
  ADMIN = 'ADMIN',
  ZONESPOC = 'SPOC',
  REQUESTER = 'REQUESTER',
  SANDBOX = 'SANDBOX',
  PRODUCTION = 'PRODUCTION'
}

export enum RequestDetailStatusEnum {
  SANDBOX_FAILED = 'SANDBOX_FAILED',
  SANDBOX_SUCCESS = 'SANDBOX_SUCCESS',
  PROD_FAILED = 'PROD_FAILED',
  PROD_SUCCESS = 'PROD_SUCCESS',
  PENDING = 'PENDING'
}

export enum TargetTypeEnum {
  PERCENT = 'PERCENT',
  AMOUNT = 'AMOUNT',
  CHANGE_SALARY_VALUE = 'CHANGE_SALARY_VALUE',
  NONE = 'NONE'
}

export enum UserRoleEnum {
  ZONESPOK = 'zonespok',
  REQUESTER = 'requester'
}

export enum RequestContextEnum {
  MY_REQUESTS = 'myRequests',
  APPROVAL = 'approval',
  REPORT = 'report'
}

// Spoc Comment Types
export type SpocCommentApiResponse = {
  comment: string;
  author: string;
  timestamp: string;
};

export enum StepStatusEnum {
  CLOSED = 'closed',
  COMPLETED = 'completed',
  AWAITING_REQUESTER_ACTION = 'awaiting-requester-action',
  PENDING_APPROVAL = 'pending-approval',
  NOT_STARTED = 'not-started',
  REJECTED = 'rejected',
  APPROVED = 'approved',
  PROCESSING = 'processing'
}

export type SpocComment = {
  comment: string;
  author: string;
  timestamp: string;
};

// Workday Error Types
export type WorkdayError = {
  faultCode: string;
  faultString: string;
  details: string | null;
  messages: string[];
};

// Compensation Plan Types
export type CompensationPlanCategory = {
  id: number;
  code: string;
};

export type CompensationPlanApiResponse = {
  id: string;
  code: string;
  allowedTargetType: TargetTypeEnum;
  category: CompensationPlanCategory;
};

// Request Detail API Response (snake_case)
export type RequestCompensationDetailApiResponse = {
  id: string;
  line_number?: number;
  status: RequestDetailStatusEnum;
  employee_id: string;
  position: string | null;
  target_type: string;
  target_value: number;
  package?: string;
  grade?: string;
  grade_profile?: string;
  gradeProfile?: string;
  step?: string;
  workday_id: string | null;
  created_at: string;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by: string | null;
  compensationPlan: CompensationPlanApiResponse;
  workday_error: WorkdayError | null;
  logs?: LogDetail[];
};

// Request Status Position Detail API Response (snake_case)
export type RequestStatusPositionDetailApiResponse = {
  id: string;
  line_number?: number;
  status: RequestDetailStatusEnum;
  employee_id: string;
  position: string | null;
  action?: string;
  reason_status_position: string;
  workday_id: string | null;
  created_at: string;
  is_deleted: boolean;
  deleted_at: string | null;
  deleted_by: string | null;
  workday_error: WorkdayError | null;
};

export type RequestUpdateApiResponse = {
  id: string;
  status: string;
  zone_code: string;
};

// API Response Types (snake_case)
export type RequestApiResponse = {
  id: string;
  eibhub_id: string;
  eib_type: string;
  request_compensation_type: string;
  created_at: string;
  created_by_name: string;
  updated_at: string;
  closed_at: string | null;
  opened_by: string;
  approved_by: {
    globalId: string;
    name: string;
  } | null;
  effective_date: string;
  employee_visibility_date: string;
  status: string;
  step: StepEnum;
  zone: string;
  prod_attempt: number;
  staging_attempt: number;
  request_items_count: number;
  reason_request_compensation?: string;
  allowed_target_type?: string;
  spoc_comments?: SpocCommentApiResponse[];
};

// Frontend Model (camelCase)
// Base type with common fields
type RequestBase = {
  id: string;
  eibHubId: string;
  requestCompensationType: string;
  createdAt: string;
  createdByName: string;
  approvedByName: string;
  updatedAt: string;
  closedAt: string | null;
  openedBy: string;
  effectiveDate: string;
  employeeVisibilityDate: string;
  status: StatusEnum;
  step: StepEnum;
  zone: string;
  prodAttempt: number;
  stagingAttempt: number;
  // Legacy fields for backward compatibility
  number?: string;
  type?: string;
  date?: string;
  createdBy?: string;
  visibilityDate?: string;
  reasonsFor?: string;
  targetType?: TargetTypeEnum;
  spocComments?: SpocComment[];
  rows?: number;
};

// Request for compensation changes
export type RequestCompensation = RequestBase & {
  eibType: EIBType.REQUEST_COMPENSATION;
  details?: RequestCompensationDetail[];
};

// Request for status position changes
export type RequestStatusPosition = RequestBase & {
  eibType: EIBType.STATUS_POSITION;
  details?: RequestStatusPositionDetail[];
};

// Discriminated union based on eibType
export type Request = RequestCompensation | RequestStatusPosition;

export type RequestPagination = {
  data: Request[];
  total: number;
  limit: number;
  page: number;
};

// Mapper function from API to Frontend model
export const mapRequestApiToFrontend = ({
  id,
  eibhub_id,
  eib_type,
  request_compensation_type,
  created_at,
  updated_at,
  closed_at,
  opened_by,
  approved_by,
  created_by_name,
  effective_date,
  employee_visibility_date,
  status,
  step,
  zone,
  prod_attempt,
  staging_attempt,
  reason_request_compensation,
  allowed_target_type,
  request_items_count,
  spoc_comments
}: RequestApiResponse): Request => {
  return {
    id,
    eibHubId: eibhub_id,
    eibType: eib_type as EIBType,
    requestCompensationType: request_compensation_type,
    createdAt: created_at,
    updatedAt: updated_at,
    closedAt: closed_at,
    openedBy: opened_by,
    approvedByName: approved_by?.name || '',
    createdByName: created_by_name,
    effectiveDate: effective_date,
    employeeVisibilityDate: employee_visibility_date,
    status: (status as StatusEnum) || StatusEnum.OPEN,
    step,
    zone,
    prodAttempt: prod_attempt,
    stagingAttempt: staging_attempt,
    number: id,
    type: eib_type,
    date: created_at,
    createdBy: opened_by,
    visibilityDate: employee_visibility_date,
    reasonsFor: reason_request_compensation,
    targetType: allowed_target_type as TargetTypeEnum,
    rows: request_items_count,
    spocComments: spoc_comments
  };
};

export const mapRequestDetailApiToFrontend = ({
  id,
  logs,
  ...apiDetail
}: RequestCompensationDetailApiResponse): RequestCompensationDetail => {
  // Format error details from workday_error if present
  let errorDetails: string | undefined;
  if (apiDetail.workday_error) {
    const messages = apiDetail.workday_error.messages.join(' ');
    errorDetails = `${apiDetail.workday_error.faultString}. ${messages}`;
  }

  return {
    id,
    lineNumber: apiDetail.line_number,
    employeeId: apiDetail.employee_id,
    position: apiDetail.position || '',
    planCode: apiDetail.compensationPlan?.code ?? undefined,
    target: apiDetail.target_value?.toString(),
    targetType: apiDetail.target_type as TargetTypeEnum,
    package: apiDetail.package,
    grade: apiDetail.grade,
    gradeProfile: apiDetail.grade_profile ?? apiDetail.gradeProfile,
    step: apiDetail.step,
    errorDetails,
    isDeleted: apiDetail.is_deleted,
    deletedAt: apiDetail.deleted_at || undefined,
    deletedBy: apiDetail.deleted_by || undefined,
    logs
  };
};

export const mapRequestStatusPositionDetailApiToFrontend = (
  apiDetail: RequestStatusPositionDetailApiResponse
): RequestStatusPositionDetail => {
  // Format error details from workday_error if present
  let errorDetails: string | undefined;
  if (apiDetail.workday_error) {
    const messages = apiDetail.workday_error.messages.join(' ');
    errorDetails = `${apiDetail.workday_error.faultString}. ${messages}`;
  }

  return {
    id: apiDetail.id,
    lineNumber: apiDetail.line_number,
    position: apiDetail.position || '',
    action: apiDetail.action as ActionsEnum | undefined,
    reason: apiDetail.reason_status_position,
    errorDetails,
    isDeleted: apiDetail.is_deleted,
    deletedAt: apiDetail.deleted_at || undefined,
    deletedBy: apiDetail.deleted_by || undefined
  };
};

export const eibTypeLabels: Record<string, string> = {
  [EIBType.REQUEST_COMPENSATION]: 'Request Compensation Change',
  [EIBType.STATUS_POSITION]: 'Status Position'
};

export const compensationTypeLabels: Record<string, string> = {
  [RequestCompensationType.SALARY_INCREASE]: 'Salary Increase',
  [RequestCompensationType.ASSIGN_PLAN]: 'Assign Plan',
  [RequestCompensationType.REMOVE_PLAN]: 'Remove Plan'
};
